import type { CSSProperties } from 'react';
import type { GameState } from '../../../engine';
import { fmt } from '../../brand';
import type { Targets } from '../../game/viewModel';
import { FlatCard } from '../Card';
import { IconEyeClosed, IconLaurel, IconToss } from '../Icons';

/** The hint's jade laurel seal, perched on the advised target's top-right corner. */
export const HintSeal = () => (
  <span className="hint-seal" aria-hidden="true">
    <IconLaurel size={24} />
  </span>
);

/**
 * Deck · holding spot · discard pile. Things you can tap glow. The only labels
 * are the two real choices: "Keep hidden", and "Discard" (the pile turns into
 * a drop target while you hold a card drawn from the deck).
 */
export function Piles({
  state,
  me,
  targets,
  pw,
  hw,
  shake,
  onDeck,
  onDiscard,
  onKeepHidden,
  hint = null,
}: {
  state: GameState;
  me: number | null;
  targets: Targets;
  pw: number;
  hw: number;
  shake: string | null;
  onDeck: () => void;
  onDiscard: () => void;
  onKeepHidden: () => void;
  /** The hint marks one of these (game/hints.ts). */
  hint?: 'deck' | 'discard' | 'keep' | null;
}) {
  const top = state.discardPile[state.discardPile.length - 1];
  const under = state.discardPile[state.discardPile.length - 2];
  const mineHolding = me !== null && state.current === me && state.stage === 'holding';
  const holding = (state.phase === 'playing' || state.phase === 'finalTurns') && state.stage === 'holding';
  const drawn = holding ? state.drawn : null;
  const tossTarget = mineHolding && targets.throwAway;
  const deckCount = state.drawPileCount;
  const anyTarget = targets.slots.size > 0;

  return (
    <div className="piles" style={{ '--pw': `${pw}px`, '--hw': `${hw}px` } as CSSProperties}>
      <button
        type="button"
        className={`pile deck${targets.deck ? ' target' : ''}${anyTarget && !targets.deck ? ' dim' : ''}${shake === 'deck' ? ' shake' : ''}${hint === 'deck' ? ' hinted' : ''}`}
        onClick={onDeck}
        data-pile="deck"
        data-coach="deck"
        aria-label={`Deck, ${deckCount} cards${targets.deck ? '. Draw from the deck.' : ''}`}
        aria-disabled={!targets.deck}
      >
        {deckCount > 2 && <span className="deck-layer l2" />}
        {deckCount > 1 && <span className="deck-layer l1" />}
        {deckCount > 0 ? <FlatCard value={null} width={pw} /> : <span className="pile-empty" style={{ width: pw }} />}
        {hint === 'deck' && <HintSeal />}
      </button>

      <div className={`held${drawn || (holding && state.drawnFrom === 'deck') ? ' has' : ''}`} data-pile="held">
        {drawn ? (
          <FlatCard value={drawn.value} width={hw} flightId={drawn.id} className="lifted" />
        ) : holding && state.drawnFrom === 'deck' ? (
          <FlatCard value={null} width={hw} className="lifted" />
        ) : targets.keepHidden ? (
          <button type="button" className={`keep-hidden${hint === 'keep' ? ' hinted' : ''}`} onClick={onKeepHidden} aria-keyshortcuts="P">
            <span className="keep-hidden-ic">
              <IconEyeClosed size={22} />
            </span>
            Keep hidden
            {hint === 'keep' && <HintSeal />}
          </button>
        ) : (
          <span className="held-empty" />
        )}
      </div>

      <button
        type="button"
        className={`pile discard${tossTarget ? ' drop' : ''}${targets.discard || tossTarget ? ' target' : ''}${
          anyTarget && !(targets.discard || tossTarget) ? ' dim' : ''
        }${shake === 'discard' ? ' shake' : ''}${hint === 'discard' ? ' hinted' : ''}`}
        onClick={onDiscard}
        data-pile="discard"
        data-coach="discard"
        aria-label={
          tossTarget
            ? 'Discard the card you drew'
            : top
              ? `Discard pile, top card ${fmt(top.value)}${targets.discard ? '. Take it.' : ''}`
              : 'Discard pile, empty'
        }
        aria-keyshortcuts={tossTarget ? 'X F' : 'F'}
        aria-disabled={!(targets.discard || tossTarget)}
      >
        {tossTarget ? (
          // Holding a deck card: the whole pile becomes one card-sized drop target (same look as Keep hidden).
          <span className="discard-drop" style={{ width: pw, height: pw * 1.5 }}>
            <span className="discard-drop-ic">
              <IconToss size={Math.round(Math.min(40, Math.max(18, pw * 0.22)))} />
            </span>
            Discard
          </span>
        ) : (
          <>
            {under && <FlatCard value={under.value} width={pw} flightId={under.id} className="under" />}
            {top ? (
              <FlatCard value={top.value} width={pw} flightId={top.id} className="top" />
            ) : (
              <span className="pile-empty" style={{ width: pw, height: pw * 1.5 }} />
            )}
          </>
        )}
        {hint === 'discard' && <HintSeal />}
      </button>
    </div>
  );
}
